/**
 * Smart Sticky Cart Bar
 *
 * Shows/hides based on scroll position
 * Syncs with variant grid selections
 *
 * UPDATED:
 * - Sticky quantity is now interactive
 * - Adds minus / quantity / plus controls
 * - Sticky quantity syncs with main Shopify quantity
 * - Main Shopify quantity changes sync back to sticky bar
 * - Subtotal updates when quantity changes
 */

(function () {
  'use strict';

  class SmartStickyCartBar {
    constructor() {
      this.bar = document.querySelector('[data-smart-sticky-bar]');

      if (!this.bar) return;

      this.variantsContainer = this.bar.querySelector(
        '[data-sticky-variants]'
      );

      this.subtotalEl = this.bar.querySelector(
        '[data-sticky-subtotal]'
      );

      this.mobileSubtotalEl = this.bar.querySelector(
        '[data-sticky-subtotal-mobile]'
      );

      this.addToCartBtn = this.bar.querySelector(
        '[data-sticky-add-to-cart]'
      );

      this.addToCartLabel =
        this.addToCartBtn?.querySelector('span');

      this.defaultButtonText =
        this.addToCartLabel?.textContent?.trim() ||
        'Add to Cart';

      this.isVisible = false;

      this.observerTarget = null;

      this.mainProduct =
        document.querySelector('.smart-main-product') ||
        document.querySelector(
          'section[id^="MainProduct-"], .product'
        );

      this.productData = this.getProductData();

      this.cartQuantities = {};

      this.cartStateReady = false;

      this.currentItems = [];

      this.currentFormattedTotal = '$0.00';

      this.init();
    }


    /* =====================================================
       PRODUCT DATA
       ===================================================== */

    getProductData() {
      const jsonEl = this.bar.querySelector(
        '[data-smart-sticky-product-json]'
      );

      if (!jsonEl) return null;

      try {
        return JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error(
          'Smart Sticky Cart Bar: Failed to parse product JSON',
          e
        );

        return null;
      }
    }


    /* =====================================================
       INITIALIZATION
       ===================================================== */

    init() {
      this.setStickyButtonState(
        true,
        this.defaultButtonText
      );

      this.setupIntersectionObserver();

      this.bindEvents();

      this.refreshCartState().then(() =>
        this.refreshCurrentSelectionState()
      );
    }


    /* =====================================================
       CART STATE
       ===================================================== */

    async refreshCartState() {
      try {
        const response = await fetch('/cart.js', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        const cart = await response.json();

        this.cartQuantities = {};

        (cart.items || []).forEach((item) => {
          this.cartQuantities[
            String(item.variant_id)
          ] =
            (
              this.cartQuantities[
                String(item.variant_id)
              ] || 0
            ) + item.quantity;
        });
      } catch (error) {
        console.warn(
          'Smart Sticky Cart Bar: Failed to read cart inventory state',
          error
        );

        this.cartQuantities = {};
      } finally {
        this.cartStateReady = true;
      }
    }


    refreshCurrentSelectionState() {
      if (
        document.querySelector(
          '[data-smart-variant-grid]'
        )
      ) {
        this.updateFromGrid({
          items: this.currentItems,
          formattedTotal:
            this.currentFormattedTotal,
        });
      } else {
        this.updateFromStandardForm();
      }
    }


    /* =====================================================
       STICKY BAR VISIBILITY
       ===================================================== */

    setupIntersectionObserver() {
      const mainAddToCart =
        document.querySelector(
          '[data-smart-add-to-cart]'
        );

      if (!mainAddToCart) {
        this.observerTarget =
          document.querySelector(
            '.product-form__submit[name="add"]'
          ) ||
          document.querySelector(
            '.product-form__buttons'
          ) ||
          document.querySelector(
            '.smart-main-product__buy-buttons'
          );
      } else {
        this.observerTarget = mainAddToCart;
      }

      if (!this.observerTarget) return;

      const observer =
        new IntersectionObserver(
          () => this.updateVisibility(),
          {
            root: null,
            threshold: 0,
            rootMargin:
              '0px 0px -12px 0px',
          }
        );

      observer.observe(this.observerTarget);

      window.addEventListener(
        'scroll',
        () => this.updateVisibility(),
        { passive: true }
      );

      window.addEventListener(
        'resize',
        () => this.updateVisibility(),
        { passive: true }
      );

      this.updateVisibility();
    }


    updateVisibility() {
      if (!this.observerTarget) return;

      const targetRect =
        this.observerTarget.getBoundingClientRect();

      const mainRect = this.mainProduct
        ? this.mainProduct.getBoundingClientRect()
        : targetRect;

      const viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight;

      const hasPassedBuyArea =
        targetRect.bottom < 0 ||
        mainRect.bottom <
          viewportHeight * 0.25;

      if (hasPassedBuyArea) {
        this.show();
      } else {
        this.hide();
      }
    }


    /* =====================================================
       EVENTS
       ===================================================== */

    bindEvents() {
      /*
       * Listen for custom variant-grid updates.
       */
      window.addEventListener(
        'smart:variant-grid:update',
        (e) => {
          this.updateFromGrid(e.detail);
        }
      );


      /*
       * Standard Shopify product forms.
       */
      this.bindStandardProductForm();


      /*
       * NEW:
       * Event delegation for dynamically-created
       * sticky quantity buttons.
       */
      if (this.variantsContainer) {
        this.variantsContainer.addEventListener(
          'click',
          (event) => {
            const minusButton =
              event.target.closest(
                '[data-sticky-quantity-minus]'
              );

            const plusButton =
              event.target.closest(
                '[data-sticky-quantity-plus]'
              );

            if (minusButton) {
              event.preventDefault();

              this.changeStickyQuantity(-1);

              return;
            }

            if (plusButton) {
              event.preventDefault();

              this.changeStickyQuantity(1);
            }
          }
        );
      }


      /*
       * Sticky Add to Cart.
       */
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener(
          'click',
          () => {
            if (
              this.isStickyButtonDisabled()
            ) {
              return;
            }

            const mainBtn =
              document.querySelector(
                '[data-smart-add-to-cart]'
              ) ||
              document.querySelector(
                '.product-form__submit[name="add"]'
              );

            if (
              mainBtn &&
              !this.isMainButtonDisabled(
                mainBtn
              )
            ) {
              mainBtn.click();
            }
          }
        );
      }
    }


    /* =====================================================
       STANDARD SHOPIFY PRODUCT FORM
       ===================================================== */

    bindStandardProductForm() {
      if (
        document.querySelector(
          '[data-smart-variant-grid]'
        )
      ) {
        return;
      }

      const sync = () =>
        this.updateFromStandardForm();

      const variantInput =
        document.querySelector(
          'form[action*="/cart/add"] .product-variant-id, form[action*="/cart/add"] [name="id"]'
        );

      const quantityInput =
        this.getMainQuantityInput();

      const variantControls =
        document.querySelector(
          'variant-selects, variant-radios'
        );

      if (variantControls) {
        variantControls.addEventListener(
          'change',
          () =>
            window.setTimeout(sync, 0)
        );
      }

      if (variantInput) {
        variantInput.addEventListener(
          'change',
          sync
        );
      }

      if (quantityInput) {
        quantityInput.addEventListener(
          'change',
          sync
        );

        quantityInput.addEventListener(
          'input',
          sync
        );
      }

      document.addEventListener(
        'change',
        (e) => {
          if (
            e.target.matches(
              'select[name^="options"], input[type="radio"][name^="options"]'
            )
          ) {
            window.setTimeout(
              sync,
              0
            );
          }
        }
      );

      sync();
    }


    /* =====================================================
       NEW: MAIN QUANTITY INPUT HELPER
       ===================================================== */

    getMainQuantityInput() {
      return document.querySelector(
        '.product-form__quantity .quantity__input, form[action*="/cart/add"] [name="quantity"]'
      );
    }


    /* =====================================================
       NEW: STICKY QUANTITY CONTROLS
       ===================================================== */

    changeStickyQuantity(change) {
      /*
       * First try to update the real Shopify
       * quantity input.
       */
      const quantityInput =
        this.getMainQuantityInput();

      /*
       * Get current quantity from the main input
       * when available.
       *
       * Otherwise fall back to the current sticky
       * item quantity.
       */
      let currentQuantity = 1;

      if (quantityInput) {
        currentQuantity =
          Math.max(
            1,
            parseInt(
              quantityInput.value || '1',
              10
            ) || 1
          );
      } else if (
        this.currentItems.length
      ) {
        currentQuantity =
          Math.max(
            1,
            parseInt(
              this.currentItems[0]
                .quantity || '1',
              10
            ) || 1
          );
      }

      /*
       * Never allow quantity below 1.
       */
      const newQuantity =
        Math.max(
          1,
          currentQuantity + change
        );


      /*
       * No change required.
       */
      if (
        newQuantity ===
        currentQuantity
      ) {
        return;
      }


      /*
       * If the normal Shopify quantity input
       * exists, update it and trigger both
       * input + change events.
       *
       * This keeps the normal product form,
       * subtotal and sticky bar synchronized.
       */
      if (quantityInput) {
        quantityInput.value =
          newQuantity;

        quantityInput.dispatchEvent(
          new Event('input', {
            bubbles: true,
          })
        );

        quantityInput.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        /*
         * Force a refresh in case the theme
         * doesn't respond to one of those
         * events.
         */
        window.setTimeout(
          () =>
            this.updateFromStandardForm(),
          0
        );

        return;
      }


      /*
       * FALLBACK:
       *
       * Some custom variant-grid implementations
       * may not expose a standard Shopify
       * quantity input.
       *
       * In that case update our current sticky
       * state directly.
       */
      if (
        this.currentItems.length
      ) {
        const updatedItems =
          this.currentItems.map(
            (item, index) => {
              if (index !== 0) {
                return item;
              }

              return {
                ...item,
                quantity:
                  newQuantity,
              };
            }
          );

        const total =
          updatedItems.reduce(
            (sum, item) => {
              const price =
                Number(
                  item.price
                ) || 0;

              const quantity =
                Math.max(
                  1,
                  parseInt(
                    item.quantity ||
                      '1',
                    10
                  ) || 1
                );

              return (
                sum +
                price * quantity
              );
            },
            0
          );

        this.updateFromGrid({
          items: updatedItems,
          formattedTotal:
            this.formatMoney(total),
        });
      }
    }


    /* =====================================================
       STANDARD FORM UPDATE
       ===================================================== */

    updateFromStandardForm() {
      const variantInput =
        document.querySelector(
          'form[action*="/cart/add"] .product-variant-id, form[action*="/cart/add"] [name="id"]'
        );

      if (!variantInput) return;

      const variant =
        this.productData?.variants?.find(
          (item) =>
            String(item.id) ===
            String(
              variantInput.value
            )
        );

      const quantityInput =
        this.getMainQuantityInput();

      const quantity =
        Math.max(
          1,
          parseInt(
            quantityInput?.value ||
              '1',
            10
          ) || 1
        );

      const total = variant
        ? variant.price *
          quantity
        : 0;

      const formattedTotal =
        this.formatMoney(
          total,
          variant?.price_formatted
        );

      this.updateFromGrid({
        items: variant
          ? [
              {
                id: variant.id,
                title:
                  variant.title,
                quantity,
                price:
                  variant.price,
                available:
                  variant.available,
                inventory_quantity:
                  variant.inventory_quantity,
                inventory_management:
                  variant.inventory_management,
                inventory_policy:
                  variant.inventory_policy,
              },
            ]
          : [],

        formattedTotal,
      });
    }


    /* =====================================================
       MONEY FORMATTING
       ===================================================== */

    formatMoney(cents, fallback) {
      if (
        !Number.isFinite(cents)
      ) {
        return (
          fallback ||
          '$0.00'
        );
      }

      return (
        '$' +
        (cents / 100).toFixed(2)
      );
    }


    /* =====================================================
       UPDATE STICKY BAR
       ===================================================== */

    updateFromGrid(detail) {
      const {
        items,
        formattedTotal = '$0.00',
      } = detail;

      const selectedItems =
        Array.isArray(items)
          ? items
          : [];

      this.currentItems =
        selectedItems;

      this.currentFormattedTotal =
        formattedTotal;


      /*
       * Update subtotal.
       */
      if (this.subtotalEl) {
        this.subtotalEl.textContent =
          formattedTotal;
      }

      if (
        this.mobileSubtotalEl
      ) {
        this.mobileSubtotalEl.textContent =
          formattedTotal;
      }


      /*
       * UPDATED:
       * Build interactive quantity / variant controls.
       */
      if (
        this.variantsContainer
      ) {
        this.variantsContainer.innerHTML =
          selectedItems
            .map((item) =>
              this.formatVariantTag(
                item
              )
            )
            .join('');
      }


      this.syncAddToCartState(
        selectedItems
      );
    }


    /* =====================================================
       ADD TO CART STATE
       ===================================================== */

    syncAddToCartState(items) {
      const mainBtn =
        document.querySelector(
          '[data-smart-add-to-cart]'
        ) ||
        document.querySelector(
          '.product-form__submit[name="add"]'
        );

      const status =
        this.getPurchaseStatus(
          items,
          mainBtn
        );

      this.setStickyButtonState(
        status.disabled,
        status.label
      );
    }


    getPurchaseStatus(
      items,
      mainBtn
    ) {
      if (!items.length) {
        return {
          disabled: true,
          label:
            this.defaultButtonText,
        };
      }

      if (
        !this.cartStateReady
      ) {
        return {
          disabled: true,
          label:
            this.defaultButtonText,
        };
      }

      if (
        mainBtn &&
        this.isMainButtonDisabled(
          mainBtn
        )
      ) {
        return {
          disabled: true,
          label:
            this.getMainButtonLabel(
              mainBtn
            ) || 'Sold Out',
        };
      }

      const unavailableItem =
        items.find(
          (item) =>
            !this.isItemPurchasable(
              item
            )
        );

      if (unavailableItem) {
        return {
          disabled: true,
          label: 'Sold Out',
        };
      }

      return {
        disabled: false,
        label:
          this.defaultButtonText,
      };
    }


    isItemPurchasable(item) {
      if (
        !item ||
        item.available === false
      ) {
        return false;
      }

      const quantity =
        Math.max(
          1,
          parseInt(
            item.quantity || '1',
            10
          ) || 1
        );

      const tracksInventory =
        Boolean(
          item.inventory_management
        );

      const inventoryPolicy =
        String(
          item.inventory_policy || ''
        ).toLowerCase();

      const inventoryQuantity =
        Number(
          item.inventory_quantity
        );

      if (
        tracksInventory &&
        inventoryPolicy ===
          'deny' &&
        Number.isFinite(
          inventoryQuantity
        )
      ) {
        const cartQuantity =
          this.cartQuantities[
            String(item.id)
          ] || 0;

        return (
          quantity +
            cartQuantity <=
          inventoryQuantity
        );
      }

      return true;
    }


    setStickyButtonState(
      disabled,
      label
    ) {
      if (!this.addToCartBtn)
        return;

      this.addToCartBtn.disabled =
        disabled;

      this.addToCartBtn.setAttribute(
        'aria-disabled',
        disabled
          ? 'true'
          : 'false'
      );

      this.addToCartBtn.classList.toggle(
        'smart-sticky-bar__add-to-cart--disabled',
        disabled
      );

      if (
        this.addToCartLabel
      ) {
        this.addToCartLabel.textContent =
          label ||
          this.defaultButtonText;
      }
    }


    isStickyButtonDisabled() {
      return (
        !this.addToCartBtn ||
        this.addToCartBtn.disabled ||
        this.addToCartBtn.getAttribute(
          'aria-disabled'
        ) === 'true'
      );
    }


    isMainButtonDisabled(
      button
    ) {
      return (
        button.disabled ||
        button.getAttribute(
          'aria-disabled'
        ) === 'true' ||
        button.classList.contains(
          'disabled'
        )
      );
    }


    getMainButtonLabel(
      button
    ) {
      return button?.textContent
        ?.replace(/\s+/g, ' ')
        .trim();
    }


    /* =====================================================
       UPDATED:
       QUANTITY / VARIANT OUTPUT
       ===================================================== */

    formatVariantTag(item) {
      const title =
        this.formatVariantTitle(
          item.title
        );

      const quantity =
        Math.max(
          1,
          parseInt(
            item.quantity || '1',
            10
          ) || 1
        );


      /*
       * NEW:
       *
       * Default Shopify products previously displayed:
       *
       * Quantity: 1
       *
       * as a plain span.
       *
       * They now get a real interactive:
       *
       * −   1   +
       *
       * quantity selector.
       */
      if (
        title === 'Quantity'
      ) {
        return `
          <div
            class="smart-sticky-bar__quantity"
            data-sticky-quantity
            role="group"
            aria-label="Quantity"
          >
            <button
              type="button"
              class="smart-sticky-bar__quantity-button smart-sticky-bar__quantity-minus"
              data-sticky-quantity-minus
              aria-label="Decrease quantity"
              ${quantity <= 1 ? 'disabled' : ''}
            >
              &minus;
            </button>

            <span
              class="smart-sticky-bar__quantity-value"
              data-sticky-quantity-value
              aria-live="polite"
            >
              ${quantity}
            </span>

            <button
              type="button"
              class="smart-sticky-bar__quantity-button smart-sticky-bar__quantity-plus"
              data-sticky-quantity-plus
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        `;
      }


      /*
       * Products with an actual variant title continue
       * using the existing variant-tag appearance.
       */
      const label =
        `${title} (${quantity})`;

      return `
        <span class="smart-sticky-bar__tag">
          ${this.escapeHtml(label)}
        </span>
      `;
    }


    formatVariantTitle(title) {
      const normalizedTitle =
        String(title || '').trim();

      return (
        normalizedTitle ===
          'Default Title' ||
        normalizedTitle === ''
      )
        ? 'Quantity'
        : normalizedTitle;
    }


    escapeHtml(value) {
      const div =
        document.createElement(
          'div'
        );

      div.textContent =
        String(value || '');

      return div.innerHTML;
    }


    /* =====================================================
       SHOW / HIDE
       ===================================================== */

    show() {
      if (this.isVisible)
        return;

      this.isVisible = true;

      this.bar.removeAttribute(
        'hidden'
      );

      /*
       * Force reflow for CSS transition.
       */
      void this.bar.offsetHeight;

      this.bar.setAttribute(
        'aria-hidden',
        'false'
      );
    }


    hide() {
      if (!this.isVisible)
        return;

      this.isVisible = false;

      this.bar.setAttribute(
        'aria-hidden',
        'true'
      );

      this.bar.addEventListener(
        'transitionend',
        () => {
          if (
            !this.isVisible
          ) {
            this.bar.setAttribute(
              'hidden',
              ''
            );
          }
        },
        { once: true }
      );
    }
  }


  /* =======================================================
     INITIALIZE
     ======================================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () =>
        new SmartStickyCartBar()
    );
  } else {
    new SmartStickyCartBar();
  }
})();